import React, { useState } from 'react';
import { SlidePanel } from '@/components/ui/slide-panel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Settings,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

import { ReferenceFieldTemplates, RefConfig } from './ReferenceFieldTemplates';
import { CustomReferenceFieldConfig } from './CustomReferenceFieldConfig';

interface ReferenceFieldBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: RefConfig) => void;
  initialConfig?: RefConfig;
}

type BuilderMode = 'templates' | 'custom';

export function ReferenceFieldBuilder({ 
  isOpen, 
  onClose, 
  onSave, 
  initialConfig 
}: ReferenceFieldBuilderProps) {
  const [mode, setMode] = useState<BuilderMode>('templates');
  const [selectedConfig, setSelectedConfig] = useState<RefConfig | null>(initialConfig || null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  
  const handleTemplateSelect = (templateKey: string, config: RefConfig) => {
    // Apply immediately for all templates (including Checklist Item Reference)
    // The Add/Edit dialog will render any inline, simplified controls (e.g., checklist item selector)
    onSave(config);
    onClose();
  };
  
  const handleCustomConfig = () => {
    setMode('custom');
  };
  
  const handleCustomSave = (config: RefConfig) => {
    setSelectedConfig(config);
    setSelectedTemplateName('Custom Configuration');
  };
  
  const handleBackToTemplates = () => {
    setMode('templates');
  };
  
  const handleSave = () => {
    if (selectedConfig) {
      onSave(selectedConfig);
      onClose();
    }
  };
  
  const handleClose = () => {
    setMode('templates');
    setSelectedConfig(null);
    setSelectedTemplateName('');
    onClose();
  };
  
  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center space-x-2">
          <Settings className="h-6 w-6" />
          <span>Reference Field Builder</span>
        </div>
      }
      width="2xl"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
    >
        
        <div className="space-y-6">
          {/* Mode Tabs */}
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            <Button
              variant={mode === 'templates' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('templates')}
              className="flex-1"
            >
              <FileText className="h-4 w-4 mr-2" />
              Templates
            </Button>
            <Button
              variant={mode === 'custom' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('custom')}
              className="flex-1"
            >
              <Settings className="h-4 w-4 mr-2" />
              Custom Config
            </Button>
          </div>
          
          {/* Content */}
          {mode === 'templates' ? (
            <ReferenceFieldTemplates
              onSelectTemplate={handleTemplateSelect}
              onCustomConfig={handleCustomConfig}
            />
          ) : (
            <CustomReferenceFieldConfig
              initialConfig={selectedConfig || undefined}
              onSave={handleCustomSave}
              onBack={handleBackToTemplates}
            />
          )}
          
          {/* Selected Configuration Preview - Only show for custom config */}
          {mode === 'custom' && selectedConfig && (
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-medium text-foreground">Custom Configuration Preview</h3>
                </div>
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Use This Configuration
                </Button>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Reference Table:</span> {selectedConfig.ref_table}
                  </div>
                  <div>
                    <span className="font-medium">Reference Column:</span> {selectedConfig.ref_col}
                  </div>
                  <div>
                    <span className="font-medium">Lookup Column:</span> {selectedConfig.ref_lookup_col || 'id'}
                  </div>
                  <div>
                    <span className="font-medium">Field Type:</span> {selectedConfig.field_type}
                  </div>
                  {selectedConfig.ref_filter_group && (
                    <div className="md:col-span-2">
                      <span className="font-medium">Filter:</span> {selectedConfig.ref_filter_group.col_name} {selectedConfig.ref_filter_group.operator} {selectedConfig.ref_filter_group.value}
                    </div>
                  )}
                  {selectedConfig.intermediate_tables && selectedConfig.intermediate_tables.length > 0 && (
                    <div className="md:col-span-2">
                      <span className="font-medium">Intermediate Tables:</span> {selectedConfig.intermediate_tables.length} table(s)
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
    </SlidePanel>
  );
}
